import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Text from 'components/text';
import api from 'utils/api';
import { PublicCourse as IPublicCourse } from './types';

/** Side gutters only once the Tailwind `container` stops being full width (≥ xl); edge-to-edge below. */
const GUTTER_SX = { px: 0, '@media (min-width: 1280px)': { px: '20px' } };

/**
 * Guest view of a public course: `/pub/edu/:course` opens the Rise cover page,
 * `/pub/edu/:course/:module` deep-links a lesson. The backend serves the course with only its
 * public lessons, so the Rise sidebar never lists anything else.
 */
const PublicCourse: React.FC = () => {
  const { t } = useTranslation();
  const { course: courseSlug, module: moduleSlug } = useParams<{
    course: string;
    module?: string;
  }>();
  const [courses, setCourses] = useState<IPublicCourse[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.edu
      .getPublic<IPublicCourse[]>()
      .then(setCourses)
      .catch(() => setError(true));
  }, []);

  const course = courses?.find((item) => item.slug === courseSlug);
  const module = moduleSlug ? course?.modules.find((item) => item.slug === moduleSlug) : undefined;

  if (error || (courses && (!course || (moduleSlug && !module)))) {
    return (
      <Box padding={3}>
        <Text>
          {t(
            'Sorry, the page you are looking for does not exist, please contact with us if you are facing an issues.'
          )}
        </Text>
      </Box>
    );
  }
  if (!course) {
    return <Box padding={3}>{t('Loading....')}</Box>;
  }

  const src =
    `/edu/edu-contents-embedding/${course.slug}/index.html` +
    (module ? `#/lessons/${module.lesson}` : '');

  return (
    <Box
      display="flex"
      flexDirection="column"
      className="container mx-auto"
      sx={{ height: 'calc(100vh - 72px)' }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        paddingY={1}
        borderBottom={1}
        borderColor="#eee"
        sx={GUTTER_SX}
      >
        <Box component={Link} to="/pub/edu" color="#555" sx={{ whiteSpace: 'nowrap' }}>
          ← {t('All public courses')}
        </Box>
        <Text color="#666" fontSize={16}>
          {course.title}
          {module && ` · ${module.title}`}
        </Text>
      </Box>
      <Box flex={1} overflow="hidden">
        {/* 1px wider than the box on each side: hides the hairline the Rise page draws at its edges. */}
        <iframe
          title="Songfund Education"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          src={src}
          style={{
            display: 'block',
            width: 'calc(100% + 2px)',
            height: '100%',
            marginLeft: '-1px',
            border: 'none',
          }}
          allowFullScreen={true}
        ></iframe>
      </Box>
    </Box>
  );
};

export default PublicCourse;
